/**
 * Deploy phase controller.
 *
 * Teams pick a planet and a landing spot, drop cans fall, units fan out, then
 * the match starts. This owns the phase's transient visuals (highlight ring,
 * spawn marker, drop-can effects, fan-out walks) and the selection flow,
 * including waiting for a remote peer's pick. Game-wide match setup (fog,
 * mode objectives, HUD) happens in `host.onComplete()`.
 *
 * Pure pieces live in core/deploy.ts; mesh construction in rendering/.
 */

import * as THREE from 'three';
import type { GameState, DeployState, PlanetState, Vec3, TeamPalette } from '../types';
import type { SceneObjects } from '../rendering/renderer';
import { updatePlayerMesh } from '../rendering/renderer';
import type { CameraState } from '../rendering/camera';
import { setCameraTarget, setCameraZoom } from '../rendering/camera';
import type { TurnSync } from '../network/sync';
import type { NetDeploySelection } from '../network/protocol';
import { createDropCanEffect, updateDropCanEffect, disposeDropCanEffect, type DropCanEffect } from '../rendering/dropCan';
import { createDeployHighlight, createDeploySpawnMarker, removeDeployVisual } from '../rendering/deployMarkers';
import {
  showDeployOverlay, updateDeployOverlay, showDeployConfirmButton, hideDeployConfirmButton,
  showDeployAnimating, hideDeployOverlay,
} from '../ui/deploy';
import {
  initDeployState, selectPlanet, getOccupiedPlanets, autoSelectBotPlanets, isDeployComplete,
  applyDeployToPlayers, computeFanOutTargets, crashedDropCanStates, deployTeamIsVisible, deployCanSeenFrom, DEPLOY_SCAN_INTERVAL,
  DEPLOY_FAN_OUT_DISTANCE, type FanOutTarget,
} from '../core/deploy';
import { setPhase, getPlayerWorldPosition, getPlayerSurfaceNormal } from '../core/gameState';
import { getSurfaceRadius } from '../core/surface';
import { SeededRandom } from '../core/random';
import { TEAMS, WALK_SPEED, DROP_CAN_STAGGER_DELAY } from '../constants';
import { vecSlerp, vecNormalize } from '../math';
import { playSpatial, rocketLaunch, craterImpact } from '../audio/sfx';

/** What the deploy phase needs from the game. Implemented by Game in main.ts. */
export interface DeployHost {
  readonly state: GameState;
  setState(next: GameState): void;
  readonly scene: SceneObjects;
  readonly cameraState: CameraState;
  readonly network: TurnSync | null;
  /** A human team's pick (local or remote) — the replay recorder keeps it. */
  recordDeploySelection?(teamIndex: number, planetIndex: number, surfaceDir: Vec3): void;
  /** Start bot worker / WASM loading while the drop animation plays. */
  warmupBots(): void;
  /** A crashed drop can is now scenery the game owns (damage, fog visibility). */
  registerCrashedCan(canId: number, group: THREE.Group): void;
  /** Viewer is under fog (fog cam, not see-everything): deploy starts fogged, cans reveal as they fly (37.2a). */
  readonly fogActive: boolean;
  /** The viewer's team — whose cans are "ours"; -1 with no local human. */
  readonly viewerTeamIndex: number;
  /** A team's flying can scans the terrain beneath it for every unit on that team (probe radius). */
  scanFromCan(teamIndex: number, pos: Vec3): void;
  /** Repaint fog colours on the planets after reveals. */
  repaintFog(): void;
  /** Positions are committed: recompute what the viewer can see (enemy units, fog, ghosts). */
  refreshFogView(): void;
  /** After refreshFogView: is this unit's mesh shown to the viewer? */
  isUnitVisibleToViewer(playerId: number): boolean;
  /** Everyone has landed and fanned out: start the match. */
  onComplete(): void;
}

interface DropEntry {
  effect: DropCanEffect;
  selectionIndex: number;
  hidden: boolean;   // hideEnemyDeploy: animate invisibly so the crashed can still exists afterwards
}

interface FanOutAnim extends FanOutTarget {
  progress: number;
  duration: number;
}

export class DeployPhaseController {
  private drops: DropEntry[] = [];
  private fanOut: FanOutAnim[] = [];
  private selectedPlanet: number | null = null;
  private selectedDir: Vec3 | null = null;
  private highlight: THREE.Mesh | null = null;
  private spawnMarker: THREE.Group | null = null;
  private finished = false;
  private scanClock = 0;   // throttles the cans' terrain scans + fog repaint
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();

  constructor(private readonly host: DeployHost) {}

  private get state(): GameState { return this.host.state; }
  private get deployState(): DeployState | undefined { return this.state.deployState; }

  /** A planet is selected but not yet confirmed (Escape cancels it). */
  get hasSelection(): boolean { return this.selectedPlanet !== null; }

  // ===========================================================================
  // FLOW
  // ===========================================================================

  /** Enter the deploy phase. Player meshes stay hidden until their drop can lands. */
  start(): void {
    this.finished = false;
    this.scanClock = 0;
    const s = this.state;
    const config = s.config;
    const deployState = initDeployState(s.players, config.numTeams, config.humanTeams);
    this.host.setState({ ...setPhase(s, 'deploying'), deployState });

    for (const [, mesh] of this.host.scene.playerMeshes) mesh.group.visible = false;
    if (this.host.fogActive) this.host.repaintFog();   // choose a planet from a fogged picture (37.2a)
    if (config.humanTeams === 0) {
      // Bots only: pick for them and go straight to the drop
      this.autoSelectBots();
      this.startDrops();
      return;
    }
    this.promptTeam(deployState.currentTeamIndex);
  }

  /** Per-frame: drop cans, then fan-out walks, then completion. */
  update(dt: number): void {
    if (this.finished || !this.deployState?.animating) return;
    if (this.drops.length > 0) this.updateDrops(dt);
    else if (this.fanOut.length > 0) this.updateFanOut(dt);
  }

  /** Dispose everything this phase created (new game / back to menu). Crashed cans are the game's. */
  reset(): void {
    for (const { effect } of this.drops) disposeDropCanEffect(effect, false);
    this.drops = [];
    this.fanOut = [];
    this.clearSelectionVisuals();
    hideDeployOverlay();
    this.selectedPlanet = null;
    this.selectedDir = null;
    this.finished = false;
  }

  // ===========================================================================
  // SELECTION
  // ===========================================================================

  /** Mouse click during deploy: first pick a planet, then a landing spot on it. */
  handleClick(e: MouseEvent): void {
    const ds = this.deployState;
    if (!ds || ds.animating) return;
    if (isDeployComplete(ds, this.state.config.numTeams)) return;

    this.ndc.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, this.host.scene.camera);

    const clickedPlanet = this.pickPlanet(getOccupiedPlanets(ds));

    if (this.selectedPlanet === null) {
      if (clickedPlanet !== null) this.selectPlanetUi(clickedPlanet, ds);
      return;
    }
    if (clickedPlanet === null) return;                       // empty space — Escape deselects
    if (clickedPlanet !== this.selectedPlanet) { this.selectPlanetUi(clickedPlanet, ds); return; }

    // Clicked the selected planet: choose the landing zone
    const planet = this.state.planets[this.selectedPlanet];
    const surfaceDir = this.pickSurfaceDir(planet, this.selectedPlanet);
    if (!surfaceDir) return;
    this.selectedDir = surfaceDir;
    this.showSpawnMarker(planet, surfaceDir, this.teamPalette(ds.currentTeamIndex).primary);
    showDeployConfirmButton(() => this.confirm());
  }

  /** Escape: back to planet selection. True when there was a selection to cancel. */
  cancelSelection(): boolean {
    const ds = this.deployState;
    if (!ds || ds.animating || this.selectedPlanet === null) return false;
    this.selectedPlanet = null;
    this.selectedDir = null;
    this.clearSelectionVisuals();
    hideDeployConfirmButton();
    this.promptTeam(ds.currentTeamIndex);
    return true;
  }

  private confirm(): void {
    const ds = this.deployState;
    if (!ds || this.selectedPlanet === null || !this.selectedDir) return;
    const teamIndex = ds.currentTeamIndex;
    this.host.network?.sendDeploySelection(teamIndex, this.selectedPlanet, this.selectedDir);
    this.applySelection(teamIndex, this.selectedPlanet, this.selectedDir);
  }

  /** Record a team's pick (local or remote) and move to the next team or the drop. */
  private applySelection(teamIndex: number, planetIndex: number, surfaceDir: Vec3): void {
    const ds = this.deployState;
    if (!ds) return;
    this.host.recordDeploySelection?.(teamIndex, planetIndex, surfaceDir);
    const updated = selectPlanet(ds, teamIndex, planetIndex, surfaceDir, false);
    this.host.setState({ ...this.state, deployState: updated });

    this.selectedPlanet = null;
    this.selectedDir = null;
    this.clearSelectionVisuals();
    hideDeployConfirmButton();

    if (updated.currentTeamIndex >= this.state.config.humanTeams) {
      // All humans have picked; bots take what's left
      this.autoSelectBots();
      this.startDrops();
      return;
    }
    this.promptTeam(updated.currentTeamIndex);
  }

  /** Show the pick UI for a human team, or wait for the remote peer if it is theirs. */
  private promptTeam(teamIndex: number): void {
    this.setCameraOverview();
    if (this.host.network && this.host.network.isRemoteTeam(teamIndex)) {
      this.waitForRemoteSelection();
      return;
    }
    const team = this.teamPalette(teamIndex);
    showDeployOverlay(team.name, team.primary, this.state.config.modeConfig.mode);
  }

  private waitForRemoteSelection(): void {
    const waitEl = document.getElementById('net-waiting-turn');
    if (waitEl) {
      waitEl.textContent = 'AWAITING OPPONENT DEPLOYMENT...';
      waitEl.style.display = '';
    }
    hideDeployOverlay();

    const net = this.host.network;
    if (!net) return;
    const buffered = net.drainDeploySelections();
    if (buffered.length > 0) {
      const msg = buffered[0];
      this.applySelection(msg.teamIndex, msg.planetIndex, msg.surfaceDir);
      return;
    }
    net.onRemoteDeploySelection = (msg: NetDeploySelection) => {
      net.onRemoteDeploySelection = null;
      if (waitEl) waitEl.style.display = 'none';
      this.applySelection(msg.teamIndex, msg.planetIndex, msg.surfaceDir);
    };
  }

  private autoSelectBots(): void {
    const ds = this.deployState;
    if (!ds) return;
    const s = this.state;
    const rng = new SeededRandom(`${s.config.seed}-deploy`);
    const withBots = autoSelectBotPlanets(ds, s.planets, s.players, s.config.numTeams, s.config.humanTeams, rng);
    this.host.setState({ ...s, deployState: withBots });
  }

  private selectPlanetUi(planetId: number, ds: DeployState): void {
    this.selectedPlanet = planetId;
    this.selectedDir = null;
    this.clearSelectionVisuals();
    hideDeployConfirmButton();

    const planet = this.state.planets[planetId];
    setCameraTarget(this.host.cameraState, planet.position);
    setCameraZoom(this.host.cameraState, planet.radius * 5);

    const team = this.teamPalette(ds.currentTeamIndex);
    updateDeployOverlay(team.name, team.primary, planet.palette.name);
    this.highlight = createDeployHighlight(this.host.scene.scene, planet, this.host.scene.camera.position);
  }

  /** Closest unoccupied, selectable planet under the cursor (click spheres make this reliable). */
  private pickPlanet(occupied: Set<number>): number | null {
    let closest: number | null = null;
    let closestDist = Infinity;
    for (const [planetId, meshData] of this.host.scene.planetMeshes) {
      if (meshData.isStar || meshData.isWormhole || occupied.has(planetId)) continue;
      const hits = this.raycaster.intersectObject(meshData.clickSphere ?? meshData.mesh);
      if (hits.length > 0 && hits[0].distance < closestDist) {
        closestDist = hits[0].distance;
        closest = planetId;
      }
    }
    return closest;
  }

  /** Surface direction under the cursor: terrain mesh first, click sphere as fallback. */
  private pickSurfaceDir(planet: PlanetState, planetId: number): Vec3 | null {
    const meshData = this.host.scene.planetMeshes.get(planetId);
    if (!meshData) return null;
    for (const target of [meshData.mesh, meshData.clickSphere]) {
      if (!target) continue;
      const hits = this.raycaster.intersectObject(target);
      if (hits.length === 0) continue;
      const p = hits[0].point;
      const dir = { x: p.x - planet.position.x, y: p.y - planet.position.y, z: p.z - planet.position.z };
      const len = Math.hypot(dir.x, dir.y, dir.z);
      if (len > 0.001) return { x: dir.x / len, y: dir.y / len, z: dir.z / len };
    }
    return null;
  }

  // ===========================================================================
  // DROP + FAN-OUT
  // ===========================================================================

  private startDrops(): void {
    const ds = this.deployState;
    if (!ds) return;
    this.host.warmupBots();
    this.host.setState({ ...this.state, deployState: { ...ds, animating: true } });
    showDeployAnimating();

    const s = this.state;
    this.drops = [];
    let stagger = 0;
    ds.selections.forEach((sel, selectionIndex) => {
      const hidden = !deployTeamIsVisible(sel.teamIndex, s.players, s.config.hideEnemyDeploy ?? false);
      const planet = s.planets[sel.planetIndex];
      const effect = createDropCanEffect(
        this.host.scene.scene, planet.position, sel.surfaceDir, planet.radius,
        this.teamPalette(sel.teamIndex), s.planets, hidden ? 0 : stagger * DROP_CAN_STAGGER_DELAY,
      );
      if (hidden) effect.rootGroup.visible = false;
      this.drops.push({ effect, selectionIndex, hidden });
      if (!hidden) stagger++;
    });

    this.setCameraOverview();
  }

  private updateDrops(dt: number): void {
    const time = performance.now() / 1000;
    const fog = this.host.fogActive;
    const viewerTeam = this.host.viewerTeamIndex;
    const selections = this.deployState?.selections ?? [];
    this.scanClock += dt;
    const scanNow = this.scanClock >= DEPLOY_SCAN_INTERVAL;
    if (scanNow) this.scanClock = 0;
    // Our eyes: the viewer team's cans that have launched (stateTime counts up from -delay)
    const eyes: Vec3[] = [];
    if (fog) {
      for (const { effect, selectionIndex } of this.drops) {
        if (selections[selectionIndex]?.teamIndex !== viewerTeam || effect.stateTime < 0 || effect.state === 'done') continue;
        const p = effect.rootGroup.position;
        eyes.push({ x: p.x, y: p.y, z: p.z });
      }
    }
    let allDone = true;
    for (const { effect, selectionIndex, hidden } of this.drops) {
      if (effect.state === 'done') continue;
      const prev = effect.state;
      const running = updateDropCanEffect(effect, dt, time);
      const sel = selections[selectionIndex];
      const cp = effect.rootGroup.position;
      // Probe-like: a flying can reveals the terrain it passes over for its team
      if (fog && scanNow && sel && effect.stateTime >= 0 && (effect.state === 'approach' || effect.state === 'descend')) {
        this.host.scanFromCan(sel.teamIndex, { x: cp.x, y: cp.y, z: cp.z });
      }
      if (hidden) {
        effect.rootGroup.visible = false;
      } else {
        // An enemy can is seen only while one of our cans has a line to it (37.2a)
        if (fog && sel && sel.teamIndex !== viewerTeam) effect.rootGroup.visible = effect.stateTime >= 0 && deployCanSeenFrom(eyes, { x: cp.x, y: cp.y, z: cp.z }, this.state.planets);
        if (!effect.approachSoundPlayed && effect.rootGroup.visible && effect.state === 'approach') {
          effect.approachSoundPlayed = true;
          const p = effect.rootGroup.position;
          playSpatial({ x: p.x, y: p.y, z: p.z }, rocketLaunch, 4);
        }
        if (!effect.landedSoundPlayed && prev === 'descend' && effect.state === 'landed') {
          effect.landedSoundPlayed = true;
          const p = effect.rootGroup.position;
          playSpatial({ x: p.x, y: p.y, z: p.z }, craterImpact, 3);
        }
      }
      if (running) allDone = false;
    }
    if (fog && scanNow) this.host.repaintFog();
    if (allDone) this.startFanOut();
  }

  /**
   * All cans have landed: place units on their planets, show them at the can,
   * start their fan-out walks, and hand the crashed cans to the game as scenery.
   * Final positions are committed to game state now, so nothing depends on animation timing.
   */
  private startFanOut(): void {
    const ds = this.deployState;
    if (!ds) return;
    const s = this.state;

    const rng = new SeededRandom(`${s.config.seed}-deploy-apply`);
    let players = applyDeployToPlayers(s.players, ds.selections, rng);
    const targets = computeFanOutTargets(players, ds.selections, s.planets);
    const duration = Math.max(0.3, DEPLOY_FAN_OUT_DISTANCE / WALK_SPEED);
    this.fanOut = targets.map(t => ({ ...t, progress: 0, duration }));
    players = players.map(p => {
      const t = targets.find(x => x.playerId === p.id);
      return t ? { ...p, surfaceDir: t.endDir } : p;
    });

    for (const t of targets) {
      const mesh = this.host.scene.playerMeshes.get(t.playerId);
      const player = players.find(p => p.id === t.playerId);
      if (!mesh || !player) continue;
      const planet = s.planets[t.planetIndex];
      updatePlayerMesh(player, mesh, this.surfacePoint(planet, t.startDir), t.startDir);
      mesh.group.visible = true;
      mesh.animCtx.state = 'waddle';
    }

    const cans = crashedDropCanStates(ds.selections, s.crashedDropCans.length);
    for (const { effect, selectionIndex, hidden } of this.drops) {
      this.host.registerCrashedCan(cans[selectionIndex].id, effect.rootGroup);
      disposeDropCanEffect(effect, true); // keep the crashed model as scenery
      if (hidden) effect.rootGroup.visible = false; // fog visibility takes over from here
    }
    this.drops = [];
    this.host.setState({ ...this.state, players, crashedDropCans: [...s.crashedDropCans, ...cans] });
    // Positions are final: under fog, show enemy units only where the viewer's team has eyes on them
    if (this.host.fogActive) {
      this.host.refreshFogView();
      for (const t of targets) {
        const mesh = this.host.scene.playerMeshes.get(t.playerId);
        if (mesh) mesh.group.visible = this.host.isUnitVisibleToViewer(t.playerId);
      }
    }
  }

  private updateFanOut(dt: number): void {
    let allDone = true;
    for (const anim of this.fanOut) {
      if (anim.progress >= 1) continue;
      anim.progress += dt / anim.duration;
      const player = this.state.players.find(p => p.id === anim.playerId);
      const mesh = this.host.scene.playerMeshes.get(anim.playerId);
      if (!player || !mesh) { anim.progress = 1; continue; }

      if (anim.progress >= 1) {
        anim.progress = 1;
        updatePlayerMesh(player, mesh, getPlayerWorldPosition(player, this.state.planets), getPlayerSurfaceNormal(player));
        mesh.animCtx.state = 'idle';
        continue;
      }
      allDone = false;

      const t = anim.progress;
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const dir = vecNormalize(vecSlerp(anim.startDir, anim.endDir, eased));
      const planet = this.state.planets[anim.planetIndex];
      updatePlayerMesh(player, mesh, this.surfacePoint(planet, dir), dir);
      mesh.group.visible = !this.host.fogActive || this.host.isUnitVisibleToViewer(anim.playerId);
    }
    if (allDone) {
      this.fanOut = [];
      this.finish();
    }
  }

  private finish(): void {
    const ds = this.deployState;
    if (!ds) return;
    this.finished = true; // update() stops here while the host spreads match setup over frames
    this.host.setState({ ...this.state, deployState: { ...ds, done: true, animating: false } });
    hideDeployOverlay();
    this.clearSelectionVisuals();
    this.host.onComplete();
  }

  // ===========================================================================
  // HELPERS
  // ===========================================================================

  private surfacePoint(planet: PlanetState, dir: Vec3): Vec3 {
    const sr = getSurfaceRadius(planet, dir);
    return { x: planet.position.x + dir.x * sr, y: planet.position.y + dir.y * sr, z: planet.position.z + dir.z * sr };
  }

  private teamPalette(teamIndex: number): TeamPalette {
    return TEAMS[teamIndex % TEAMS.length];
  }

  private showSpawnMarker(planet: PlanetState, surfaceDir: Vec3, color: string): void {
    if (this.spawnMarker) removeDeployVisual(this.host.scene.scene, this.spawnMarker);
    this.spawnMarker = createDeploySpawnMarker(this.host.scene.scene, planet, surfaceDir, color);
  }

  private clearSelectionVisuals(): void {
    if (this.highlight) { removeDeployVisual(this.host.scene.scene, this.highlight); this.highlight = null; }
    if (this.spawnMarker) { removeDeployVisual(this.host.scene.scene, this.spawnMarker); this.spawnMarker = null; }
  }

  /** Frame the whole system: centroid target, zoom to fit the farthest planet edge. */
  private setCameraOverview(): void {
    const planets = this.state.planets.filter(p => p.type === 'planet' && p.alive);
    if (planets.length === 0) return;
    const c = { x: 0, y: 0, z: 0 };
    for (const p of planets) { c.x += p.position.x; c.y += p.position.y; c.z += p.position.z; }
    c.x /= planets.length; c.y /= planets.length; c.z /= planets.length;
    setCameraTarget(this.host.cameraState, c);
    let maxExtent = 0;
    for (const p of planets) {
      const d = Math.hypot(p.position.x - c.x, p.position.y - c.y, p.position.z - c.z) + p.radius;
      if (d > maxExtent) maxExtent = d;
    }
    setCameraZoom(this.host.cameraState, Math.max(80, maxExtent * 2.5)); // ~2.5× extent fits a 50° FOV
  }
}
